import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faCalendarAlt,
  faChartLine,
  faFileCsv,
  faFileExcel,
  faFilter,
  faSearch,
  faSpinner,
  faSyncAlt,
  faTable,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const toDateInput = (date) => {
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
};

const defaultFilters = () => {
  const today = toDateInput(new Date());
  return {
    date_from: today,
    date_to: today,
    from_time: "00:00",
    to_time: "23:55",
    search: "",
  };
};

const perPageOptions = [25, 50, 100, 250, 500, 1000];

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatCell = (value, column) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.amount) return formatAmount(value);
  return String(value);
};

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [activeKey, setActiveKey] = useState("processed-claims");
  const [category, setCategory] = useState("All");
  const [reportSearch, setReportSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total_records: 0,
    total_pages: 1,
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem("authentication/token");

  const activeReport = useMemo(
    () => reports.find((report) => report.key === activeKey) || reports[0] || null,
    [activeKey, reports]
  );

  const categories = useMemo(() => {
    const values = Array.from(new Set(reports.map((report) => report.category).filter(Boolean)));
    return ["All", ...values];
  }, [reports]);

  const filteredReports = useMemo(() => {
    const needle = reportSearch.trim().toLowerCase();
    return reports.filter((report) => {
      const categoryMatch = category === "All" || report.category === category;
      const searchMatch =
        needle === "" ||
        report.title.toLowerCase().includes(needle) ||
        report.category.toLowerCase().includes(needle);
      return categoryMatch && searchMatch;
    });
  }, [category, reportSearch, reports]);

  const requestParams = useCallback(
    (includePagination = true) => {
      const params = {
        ...appliedFilters,
      };

      if (includePagination) {
        params.page = page;
        params.per_page = perPage;
      }

      if (activeReport?.date_mode !== "datetime") {
        delete params.from_time;
        delete params.to_time;
      }

      if (activeReport?.date_mode === "none") {
        delete params.date_from;
        delete params.date_to;
        delete params.from_time;
        delete params.to_time;
      }

      Object.keys(params).forEach((key) => {
        if (params[key] === "") delete params[key];
      });

      return params;
    },
    [activeReport?.date_mode, appliedFilters, page, perPage]
  );

  const validateFilters = useCallback((sourceFilters) => {
    if (!sourceFilters.date_from || !sourceFilters.date_to) {
      toast.error("Date From and Date To are required.");
      return false;
    }
    if (sourceFilters.date_from > sourceFilters.date_to) {
      toast.error("Date To must be on or after Date From.");
      return false;
    }
    return true;
  }, []);

  const loadDefinitions = useCallback(async () => {
    try {
      setLoadingReports(true);
      const response = await axios.get(`${BASE_URL}/reports/definitions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const definitions = response.data?.data || [];
      setReports(definitions);
      setActiveKey((current) =>
        definitions.length && !definitions.some((report) => report.key === current)
          ? definitions[0].key
          : current
      );
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load report definitions."));
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, [token]);

  const fetchRows = useCallback(async () => {
    if (!activeReport) return;
    if (activeReport.date_mode !== "none" && !validateFilters(appliedFilters)) return;

    try {
      setLoadingRows(true);
      const response = await axios.get(`${BASE_URL}/reports/${activeReport.key}`, {
        params: requestParams(true),
        headers: { Authorization: `Bearer ${token}` },
      });

      setRows(response.data?.data || []);
      setSummary(response.data?.summary || {});
      setPagination(
        response.data?.pagination || {
          current_page: 1,
          per_page: perPage,
          total_records: 0,
          total_pages: 1,
        }
      );
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load report data."));
      setRows([]);
      setSummary({});
      setPagination({
        current_page: 1,
        per_page: perPage,
        total_records: 0,
        total_pages: 1,
      });
    } finally {
      setLoadingRows(false);
    }
  }, [activeReport, appliedFilters, perPage, requestParams, token, validateFilters]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const columns = useMemo(() => activeReport?.columns || [], [activeReport]);
  const totalPages = Math.max(1, Number(pagination.total_pages || 1));
  const totalRecords = Number(pagination.total_records || 0);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, totalRecords);
    return `Showing ${formatNumber(start)} to ${formatNumber(end)} of ${formatNumber(totalRecords)}`;
  }, [page, perPage, totalRecords]);

  const pageAmountText = useMemo(() => {
    const amounts = summary.page_amounts || {};
    const amountColumns = columns.filter((column) => column.amount && amounts[column.key]);
    if (!amountColumns.length) return null;
    return amountColumns.map((column) => `${column.label}: ${formatAmount(amounts[column.key])}`).join(" | ");
  }, [columns, summary.page_amounts]);

  const applyFilters = () => {
    if (activeReport?.date_mode !== "none" && !validateFilters(filters)) return;
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    const defaults = defaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setPage(1);
  };

  const selectReport = (reportKey) => {
    setActiveKey(reportKey);
    setPage(1);
    setRows([]);
  };

  const handleExportCsv = async () => {
    if (!activeReport) return;
    if (activeReport.date_mode !== "none" && !validateFilters(appliedFilters)) return;

    try {
      setExporting(true);
      const response = await axios.get(`${BASE_URL}/reports/${activeReport.key}/export`, {
        params: requestParams(false),
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const disposition = response.headers?.["content-disposition"] || "";
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i);
      const fileName = matchedFileName?.[1] || `${activeReport.key}_${Date.now()}.csv`;
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to export CSV."));
    } finally {
      setExporting(false);
    }
  };

  const handleExportXlsx = () => {
    if (!activeReport || !rows.length) return;
    const exportRows = rows.map((row) => {
      const normalized = {};
      columns.forEach((column) => {
        normalized[column.label] = row?.[column.key] ?? "";
      });
      return normalized;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `${activeReport.key}_${Date.now()}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase text-emerald-700">
            <FontAwesomeIcon icon={faChartLine} />
            <span>System</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-950">Reports</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchRows}
            disabled={loadingRows || !activeReport}
            title="Refresh"
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={loadingRows ? faSpinner : faSyncAlt} className={loadingRows ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting || !activeReport}
            title="Export CSV"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={exporting ? faSpinner : faFileCsv} className={exporting ? "animate-spin" : ""} />
            Export CSV
          </button>
          <button
            type="button"
            onClick={handleExportXlsx}
            disabled={!rows.length}
            title="Export current page to Excel"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faFileExcel} />
            Page XLSX
          </button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Active Report</p>
          <p className="mt-2 break-words text-lg font-bold text-slate-950">{activeReport?.title || "Loading..."}</p>
          <p className="mt-1 text-sm text-slate-500">{activeReport?.category || "-"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Records</p>
          <p className="mt-2 text-lg font-bold text-slate-950">{formatNumber(totalRecords)}</p>
          <p className="mt-1 text-sm text-slate-500">{showingText}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <p className="text-xs font-semibold uppercase text-slate-500">Page Totals</p>
          <p className="mt-2 break-words text-lg font-bold text-slate-950">{pageAmountText || "-"}</p>
          <p className="mt-1 text-sm text-slate-500">Page {formatNumber(page)} of {formatNumber(totalPages)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <label className="mb-2 block text-xs font-semibold uppercase text-slate-500">Find Report</label>
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
              <input
                value={reportSearch}
                onChange={(event) => setReportSearch(event.target.value)}
                className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${
                    category === item
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[640px] overflow-y-auto p-2">
            {loadingReports ? (
              <div className="flex h-28 items-center justify-center text-sm text-slate-500">
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                Loading reports
              </div>
            ) : (
              filteredReports.map((report) => (
                <button
                  key={report.key}
                  type="button"
                  onClick={() => selectReport(report.key)}
                  className={`mb-1 w-full rounded-md px-3 py-2 text-left transition ${
                    activeReport?.key === report.key
                      ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faTable} className="mt-1 text-xs" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{report.title}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{report.category}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <main className="min-w-0">
          <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 text-white">
                <FontAwesomeIcon icon={faFilter} />
              </span>
              Filters
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
              {activeReport?.date_mode !== "none" && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Date From</span>
                    <input
                      type="date"
                      value={filters.date_from}
                      onChange={(event) => setFilters((previous) => ({ ...previous, date_from: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Date To</span>
                    <input
                      type="date"
                      value={filters.date_to}
                      onChange={(event) => setFilters((previous) => ({ ...previous, date_to: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>
                </>
              )}

              {activeReport?.date_mode === "datetime" && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Time From</span>
                    <input
                      type="time"
                      value={filters.from_time}
                      step="300"
                      onChange={(event) => setFilters((previous) => ({ ...previous, from_time: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Time To</span>
                    <input
                      type="time"
                      value={filters.to_time}
                      step="300"
                      onChange={(event) => setFilters((previous) => ({ ...previous, to_time: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </label>
                </>
              )}

              <label className={`block ${activeReport?.date_mode === "none" ? "md:col-span-2 xl:col-span-4" : "xl:col-span-2"}`}>
                <span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Search</span>
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                  <input
                    value={filters.search}
                    onChange={(event) => setFilters((previous) => ({ ...previous, search: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters();
                    }}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>{activeReport?.date_mode === "none" ? "As of current database date" : `${appliedFilters.date_from} to ${appliedFilters.date_to}`}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Run Report
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-950">{activeReport?.title || "Report"}</h2>
                <p className="mt-1 text-sm text-slate-500">{showingText}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Rows</span>
                <select
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                >
                  {perPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative min-h-[360px] overflow-x-auto">
              {loadingRows && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-sm font-semibold text-slate-600">
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  Loading report
                </div>
              )}

              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-[1] bg-slate-100">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-normal text-slate-600 ${
                          column.amount || column.numeric ? "text-right" : ""
                        }`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!loadingRows && rows.length === 0 ? (
                    <tr>
                      <td colSpan={Math.max(1, columns.length)} className="px-4 py-16 text-center text-sm text-slate-500">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIndex) => (
                      <tr key={`${rowIndex}-${row?.trans_no || row?.loa_no || row?.Processor || row?.agents || ""}`} className="hover:bg-emerald-50/50">
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={`max-w-[360px] border-b border-slate-100 px-3 py-2 align-top text-slate-700 ${
                              column.amount || column.numeric ? "text-right tabular-nums" : ""
                            }`}
                          >
                            <span className="break-words">{formatCell(row?.[column.key], column)}</span>
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 p-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-500">{showingText}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                  disabled={page <= 1 || loadingRows}
                  title="Previous page"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <span className="min-w-[110px] text-center text-sm font-semibold text-slate-700">
                  {formatNumber(page)} / {formatNumber(totalPages)}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                  disabled={page >= totalPages || loadingRows}
                  title="Next page"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ReportsPage;
